import { useState, useEffect } from 'react';
import { BookmarkSimple, Trash, ArrowSquareOut, X } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

export const SavedSourcesPanel = ({ open, onClose, onSourceDeleted }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) fetchSources();
  }, [open]);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/assistant/sources`, { withCredentials: true });
      setSources(res.data.sources || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleDelete = async (sourceId, url) => {
    try {
      await axios.delete(`${API_URL}/api/assistant/sources/${sourceId}`, { withCredentials: true });
      setSources(prev => prev.filter(s => s.source_id !== sourceId));
      onSourceDeleted?.(url);
    } catch { /* ignore */ }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="saved-sources-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-sm bg-white shadow-xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f2f4f6]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#fef3c7] flex items-center justify-center">
              <BookmarkSimple size={16} className="text-[#f59e0b]" weight="fill" />
            </div>
            <div>
              <p className="font-bold text-sm text-[#2d3338]">Saved Sources</p>
              <p className="text-[10px] text-[#acb3b8]">{sources.length} source{sources.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[#f2f4f6] text-[#596065] transition-colors"
            data-testid="close-saved-sources"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Sources list */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#acb3b8] text-sm">Loading...</div>
          ) : sources.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <BookmarkSimple size={32} className="text-[#e2e8f0] mb-3" weight="duotone" />
              <p className="text-sm font-medium text-[#596065]">No saved sources yet</p>
              <p className="text-xs text-[#acb3b8] mt-1 max-w-[200px]">
                Click the bookmark icon on any citation to save it here
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sources.map((src) => {
                const domain = src.domain || src.url;
                return (
                  <div
                    key={src.source_id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#fafbfc] border border-[#f2f4f6] hover:border-[#e2e8f0] transition-colors group"
                    data-testid={`saved-source-${src.source_id}`}
                  >
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
                      alt=""
                      className="w-5 h-5 rounded flex-shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="flex-1 min-w-0">
                      <a
                        href={src.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-[#2d3338] hover:text-[#0369a1] transition-colors flex items-center gap-1"
                      >
                        <span className="truncate">{domain}</span>
                        <ArrowSquareOut size={10} className="text-[#acb3b8] flex-shrink-0" />
                      </a>
                      <p className="text-[10px] text-[#acb3b8] mt-0.5">
                        {formatDate(src.saved_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(src.source_id, src.url)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#fee2e2] text-[#acb3b8] hover:text-[#ef4444] transition-all"
                      data-testid={`delete-source-${src.source_id}`}
                      title="Remove"
                    >
                      <Trash size={13} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const formatDate = (ts) => {
  try {
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};
